import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/api';
import CompetitorWordHistory from './CompetitorWordHistory';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  LineController,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Chart } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  LineController,
  Title,
  Tooltip,
  Legend
);

const CompetitorAnalysis = ({ competitor, onBack }) => {
  const { user } = useAuth();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showWordHistory, setShowWordHistory] = useState(false);

  useEffect(() => {
    loadAnalysis();
  }, [competitor]);

  const loadAnalysis = async () => {
    if (!competitor || !user) return;

    try {
      setLoading(true);
      setError(null);

      const response = await ApiService.getCompetitorAnalysis(
        competitor.competitor_id,
        user.user_id
      );

      if (response.competitor_id) {
        setAnalysis(response);
      } else {
        setError('Failed to load competitor analysis');
      }
    } catch (err) {
      setError('Failed to load competitor analysis: ' + err.message);
      console.error('Error loading competitor analysis:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getErrorRateColor = (errorRate) => {
    if (errorRate >= 70) return '#dc3545'; // Red
    if (errorRate >= 50) return '#fd7e14'; // Orange
    if (errorRate >= 30) return '#ffc107'; // Yellow
    return '#28a745'; // Green
  };

  const prepareChartData = (dailyAccuracy) => {
    if (!dailyAccuracy || dailyAccuracy.length === 0) {
      return null;
    }

    const labels = dailyAccuracy.map(day => {
      const date = new Date(day.date);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    });

    const accuracyData = dailyAccuracy.map(day => day.accuracy);
    const attemptCounts = dailyAccuracy.map(day => day.total_attempts);

    // Find max attempts for scaling
    const maxAttempts = Math.max(...attemptCounts);

    return {
      labels,
      datasets: [
        // Background bars for attempt count
        {
          type: 'bar',
          label: 'Attempts',
          data: attemptCounts,
          backgroundColor: 'rgba(0, 123, 255, 0.1)',
          borderColor: 'rgba(0, 123, 255, 0.3)',
          borderWidth: 1,
          yAxisID: 'y1',
          order: 2,
          barThickness: 'flex',
          maxBarThickness: 40
        },
        // Primary accuracy line
        {
          type: 'line',
          label: 'Accuracy %',
          data: accuracyData,
          borderColor: '#007bff',
          backgroundColor: '#007bff',
          borderWidth: 3,
          pointBackgroundColor: '#007bff',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 6,
          pointHoverRadius: 8,
          fill: false,
          tension: 0.2,
          yAxisID: 'y',
          order: 1
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: 'Daily Accuracy Trend with Attempt Volume',
        font: {
          size: 16,
          weight: 'bold'
        },
        color: '#333',
        padding: 20
      },
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          pointStyle: 'line',
          filter: function(item, chart) {
            // Show both datasets in legend
            return true;
          }
        }
      },
      tooltip: {
        callbacks: {
          title: function(tooltipItems) {
            // Show the date as title
            return tooltipItems[0].label;
          },
          label: function(context) {
            const dataIndex = context.dataIndex;
            const dayData = analysis?.daily_accuracy[dataIndex];

            if (context.dataset.type === 'line') {
              // Accuracy line tooltip
              if (dayData) {
                const confidenceLevel = dayData.total_attempts >= 10 ? 'High' :
                                       dayData.total_attempts >= 5 ? 'Medium' : 'Low';
                return [
                  `Accuracy: ${context.parsed.y}%`,
                  `Confidence: ${confidenceLevel} (${dayData.total_attempts} attempts)`,
                  `Breakdown: ${dayData.correct} correct, ${dayData.unsure} unsure, ${dayData.wrong} wrong`
                ];
              }
              return `Accuracy: ${context.parsed.y}%`;
            } else {
              // Attempt count bar tooltip
              return `Attempts: ${context.parsed.y}`;
            }
          }
        },
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: '#007bff',
        borderWidth: 1,
        titleFont: {
          weight: 'bold',
          size: 13
        },
        bodyFont: {
          size: 12
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Date',
          color: '#666',
          font: {
            size: 12,
            weight: 'bold'
          }
        },
        grid: {
          display: false
        },
        ticks: {
          color: '#666'
        }
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Accuracy (%)',
          color: '#007bff',
          font: {
            size: 12,
            weight: 'bold'
          }
        },
        min: 0,
        max: 100,
        grid: {
          color: 'rgba(0, 123, 255, 0.1)'
        },
        ticks: {
          color: '#007bff',
          callback: function(value) {
            return value + '%';
          }
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Attempts',
          color: 'rgba(0, 123, 255, 0.6)',
          font: {
            size: 12,
            weight: 'bold'
          }
        },
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          color: 'rgba(0, 123, 255, 0.6)',
          callback: function(value) {
            return Math.floor(value);
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    },
    elements: {
      point: {
        hoverBackgroundColor: '#007bff',
        hoverBorderColor: 'white'
      }
    }
  };

  // Show word history view if requested
  if (showWordHistory) {
    return (
      <div className="card">
        <CompetitorWordHistory
          competitor={competitor}
          onBack={() => setShowWordHistory(false)}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="card">
        <div className="loading">Loading competitor analysis...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="error">
          <h3>Error</h3>
          <p>{error}</p>
          <button
            onClick={loadAnalysis}
            className="primary-button"
            style={{ marginTop: '10px' }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="analysis-header">
        <button onClick={onBack} className="back-button">
          ← Back
        </button>
        <h2 className="card-title">Analysis for {competitor.name}</h2>
        <p className="card-subtitle">Most challenging words and performance insights</p>
        <button
          onClick={() => setShowWordHistory(true)}
          className="primary-button"
          style={{ marginTop: '15px' }}
        >
          View All Practice Words
        </button>
      </div>

      {analysis && (
        <>
          {/* Daily Accuracy Chart */}
          {analysis.daily_accuracy && analysis.daily_accuracy.length > 0 && (
            <div className="chart-section">
              <div className="chart-container">
                <Chart
                  type="line"
                  data={prepareChartData(analysis.daily_accuracy)}
                  options={chartOptions}
                />
              </div>
              <p className="chart-note">
                Shows daily accuracy trends (blue line) with attempt volume (light blue bars) over the last {analysis.daily_accuracy.length} days.
                Higher bars indicate more reliable accuracy measurements. Both "correct" and "unsure" count as correct.
                {analysis.daily_accuracy.length >= 14 && " Displaying last 14 days."}
              </p>
            </div>
          )}

          {/* Overall Stats */}
          <div className="stats-overview">
            <div className="stat-item">
              <div className="stat-value">{analysis.total_attempts}</div>
              <div className="stat-label">Total Attempts</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{analysis.total_words}</div>
              <div className="stat-label">Words Practiced</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{analysis.overall_stats.accuracy_rate}%</div>
              <div className="stat-label">Accuracy Rate</div>
            </div>
            <div className="stat-item">
              <div className="stat-value correct">{analysis.overall_stats.correct}</div>
              <div className="stat-label">Correct</div>
            </div>
            <div className="stat-item">
              <div className="stat-value wrong">{analysis.overall_stats.wrong}</div>
              <div className="stat-label">Wrong</div>
            </div>
            <div className="stat-item">
              <div className="stat-value unsure">{analysis.overall_stats.unsure}</div>
              <div className="stat-label">Unsure</div>
            </div>
          </div>

          {/* Most Difficult Words */}
          <div className="difficult-words-section">
            <h3>Most Challenging Words</h3>
            {analysis.most_difficult_words.length === 0 ? (
              <div className="empty-state">
                <p>Not enough data yet!</p>
                <p style={{ fontSize: '0.9rem', color: '#666' }}>
                  Words need at least 2 attempts to appear in this analysis.
                </p>
              </div>
            ) : (
              <div className="words-list">
                {analysis.most_difficult_words.map((wordData, index) => (
                  <div key={wordData.word} className="word-card">
                    <div className="word-rank">#{index + 1}</div>
                    <div className="word-content">
                      <div className="word-header">
                        <h4 className="word-title">{wordData.word}</h4>
                        <div className="word-score">
                          <span
                            className="error-rate"
                            style={{ color: getErrorRateColor(wordData.error_rate) }}
                          >
                            {wordData.error_rate}% error rate
                          </span>
                        </div>
                      </div>

                      <div className="word-stats">
                        <div className="word-attempts">
                          <span className="attempts-total">{wordData.total_attempts} attempts:</span>
                          <span className="attempts-breakdown">
                            <span className="correct">{wordData.correct} ✓</span>
                            <span className="wrong">{wordData.wrong} ✗</span>
                            <span className="unsure">{wordData.unsure} ?</span>
                          </span>
                        </div>

                        <div className="word-timeline">
                          <span className="timeline-info">
                            {wordData.days_practicing === 0 ? 'First practiced today' :
                             wordData.days_practicing === 1 ? 'Started yesterday' :
                             `${wordData.days_practicing} days of practice`}
                          </span>
                          <span className="timeline-dates">
                            {formatDate(wordData.first_attempt)} → {formatDate(wordData.last_attempt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <style jsx>{`
        .analysis-header {
          margin-bottom: 30px;
        }

        .chart-section {
          margin-bottom: 30px;
          background: white;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 20px;
        }

        .chart-container {
          height: 300px;
          margin-bottom: 15px;
        }

        .chart-note {
          font-size: 0.85rem;
          color: #666;
          text-align: center;
          margin: 0;
          font-style: italic;
        }

        .back-button {
          background: none;
          border: none;
          color: #007bff;
          cursor: pointer;
          font-size: 1rem;
          padding: 5px 0;
          margin-bottom: 15px;
          transition: color 0.2s;
        }

        .back-button:hover {
          color: #0056b3;
        }

        .stats-overview {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 15px;
          margin-bottom: 30px;
          padding: 20px;
          background: #f8f9fa;
          border-radius: 8px;
        }

        .stat-item {
          text-align: center;
        }

        .stat-value {
          font-size: 1.8rem;
          font-weight: bold;
          color: #333;
          margin-bottom: 5px;
        }

        .stat-value.correct {
          color: #28a745;
        }

        .stat-value.wrong {
          color: #dc3545;
        }

        .stat-value.unsure {
          color: #fd7e14;
        }

        .stat-label {
          font-size: 0.85rem;
          color: #666;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .difficult-words-section h3 {
          margin-bottom: 20px;
          color: #333;
          border-bottom: 2px solid #007bff;
          padding-bottom: 8px;
        }

        .words-list {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .word-card {
          display: flex;
          align-items: flex-start;
          padding: 20px;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          background: white;
          transition: all 0.2s ease;
        }

        .word-card:hover {
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          border-color: #007bff;
        }

        .word-rank {
          background: #007bff;
          color: white;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 0.9rem;
          margin-right: 15px;
          flex-shrink: 0;
        }

        .word-content {
          flex: 1;
        }

        .word-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
        }

        .word-title {
          font-size: 1.3rem;
          color: #333;
          margin: 0;
          font-weight: 600;
        }

        .error-rate {
          font-weight: 600;
          font-size: 0.9rem;
        }

        .word-stats {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .word-attempts {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .attempts-total {
          color: #666;
          font-size: 0.9rem;
        }

        .attempts-breakdown {
          display: flex;
          gap: 12px;
        }

        .attempts-breakdown .correct {
          color: #28a745;
          font-weight: 600;
        }

        .attempts-breakdown .wrong {
          color: #dc3545;
          font-weight: 600;
        }

        .attempts-breakdown .unsure {
          color: #fd7e14;
          font-weight: 600;
        }

        .word-timeline {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.85rem;
          color: #666;
        }

        .timeline-info {
          font-style: italic;
        }

        .timeline-dates {
          font-family: monospace;
        }

        .empty-state {
          text-align: center;
          padding: 40px 20px;
          color: #666;
        }

        .empty-state p:first-child {
          font-size: 1.2rem;
          color: #333;
          margin-bottom: 10px;
        }

        .loading {
          text-align: center;
          padding: 40px;
          color: #666;
        }

        .error {
          text-align: center;
          color: #dc3545;
        }

        .primary-button {
          background-color: #007bff;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 1rem;
          transition: background-color 0.2s;
        }

        .primary-button:hover {
          background-color: #0056b3;
        }

        @media (max-width: 768px) {
          .chart-container {
            height: 250px;
          }

          .chart-note {
            font-size: 0.8rem;
            line-height: 1.3;
          }

          .stats-overview {
            grid-template-columns: repeat(3, 1fr);
            gap: 10px;
            padding: 15px;
          }

          .stat-value {
            font-size: 1.4rem;
          }

          .stat-label {
            font-size: 0.75rem;
          }

          .word-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          .word-timeline {
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
          }

          .word-card {
            padding: 15px;
          }

          .chart-section {
            padding: 15px;
          }
        }
      `}</style>
    </div>
  );
};

export default CompetitorAnalysis;